import type { IFilesystem, Hooks, ToolUse } from '../src/index';
import { sanitizeLabel } from './util';
import { diffLines, formatDiff } from './diff';

/**
 * Within-session checkpoint/rewind for the CLI. The undo primitive (`OverlayFilesystem`) commits
 * one-way, so instead of buffering we capture, per turn, the **pre-mutation content** of every file
 * a structured mutator (Write/Edit/MultiEdit/ApplyEdits) is about to change — via a `preToolUse` hook — and can
 * restore the working tree to before any earlier turn.
 *
 * Scope (v1): structured FS edits only. `bash`/`Shell` side-effects are NOT checkpointed (we can't
 * know what an arbitrary process touched) — same edit-focused boundary as CC's rewind. History is
 * bounded and in-memory: it's gone when the session ends (git is the cross-session net).
 */
export const MUTATORS = new Set(['Write', 'Edit', 'MultiEdit', 'ApplyEdits']);

/** Paths a mutator tool call is about to change. Covers single-file tools (`args.path`: Write/Edit/
 *  MultiEdit) AND the multi-file `ApplyEdits` (`args.edits[].path`). Shared by both checkpoint backends
 *  so a new write-class tool only has to be added to MUTATORS once. */
export function mutatedPaths(call: ToolUse): string[] {
  if (!MUTATORS.has(call.name)) return [];
  const out: string[] = [];
  if (typeof call.args?.path === 'string') out.push(call.args.path);
  if (Array.isArray(call.args?.edits)) for (const e of call.args.edits) if (typeof e?.path === 'string') out.push(e.path);
  return [...new Set(out)];
}

/**
 * Common contract for the CLI's two checkpoint backends: the in-memory {@link CheckpointStack}
 * (sandbox mode) and the durable git-backed `GitCheckpoints` (disk mode). Both expose the same
 * begin/list/rewindTo/size surface consumed by the REPL (`/rewind`, `/undo`, double-Esc rewind).
 */
export interface Checkpoints {
  /** Open a restore point for the turn about to run (label = the user's message). */
  begin(label: string): void | Promise<void>;
  /** Restore points, newest-first, for an interactive picker. */
  list(): { index: number; label: string; at: number; files: number }[];
  /** Restore the working tree to BEFORE restore point `index` (undo it and every later one). */
  rewindTo(index: number): Promise<{ restored: number; deleted: number }>;
  /** Number of restore points currently held. */
  readonly size: number;
  /** Optional pre-mutation hook (in-memory backend only; git captures at the turn boundary). */
  hooks?(): Hooks;
  /** Re-bind to a different conversation (resume/switch); no-op for the in-memory backend. */
  use?(sessionId: string): void;
  /** Sync `list()`/`size` with the durable backend before a read (git backend only). */
  refresh?(): Promise<void>;
  /** Unified diff of everything this session changed (oldest restore point → current tree), for `/diff`. */
  diff?(): Promise<string>;
}

interface Frame {
  label: string;
  at: number;
  /** path -> content BEFORE this turn first touched it (null = file did not exist yet). */
  saved: Map<string, string | null>;
}

export class CheckpointStack implements Checkpoints {
  private frames: Frame[] = [];
  private current?: Frame;

  constructor(private fs: IFilesystem, private max = 50) {}

  /** Open a new turn frame (call right before sending a user turn). */
  begin(label: string): void {
    this.current = { label: sanitizeLabel(label) || '(turn)', at: Date.now(), saved: new Map() };
    this.frames.push(this.current);
    if (this.frames.length > this.max) this.frames.shift(); // bounded history
  }

  /** Hook that snapshots a file's prior content before a mutating tool overwrites it. */
  hooks(): Hooks {
    return {
      preToolUse: async (call: ToolUse) => {
        if (!this.current) return;
        for (const path of mutatedPaths(call)) {
          if (this.current.saved.has(path)) continue; // only the FIRST touch in a turn matters
          let prior: string | null = null;
          try { prior = await this.fs.readFile(path); } catch { prior = null; } // absent → created this turn
          this.current.saved.set(path, prior);
        }
      },
    };
  }

  get size(): number { return this.frames.length; }

  /** Frames newest-first, for an interactive picker. */
  list(): { index: number; label: string; at: number; files: number }[] {
    return this.frames.map((f, i) => ({ index: i, label: f.label, at: f.at, files: f.saved.size })).reverse();
  }

  /** Unified-style diff of all session edits: each file's OLDEST saved content vs its current content. */
  async diff(): Promise<string> {
    const base = new Map<string, string | null>(); // oldest frame wins = true pre-session content
    for (const f of this.frames) for (const [path, prior] of f.saved) if (!base.has(path)) base.set(path, prior);
    const parts: string[] = [];
    for (const [path, prior] of base) {
      let now: string | null = null;
      try { now = await this.fs.readFile(path); } catch { /* deleted since */ }
      if ((prior ?? '') === (now ?? '')) continue;
      const ops = diffLines(prior ?? '', now ?? '');
      parts.push(`--- ${path}${prior == null ? ' (new)' : now == null ? ' (deleted)' : ''}\n${formatDiff(ops)}`);
    }
    return parts.join('\n');
  }

  /**
   * Restore the working tree to BEFORE frame `index` — undo that frame and every later one.
   * Frames are replayed newest→oldest so the OLDEST saved content for a path wins (its true
   * pre-edit state). A file that was created in an undone frame is deleted.
   */
  async rewindTo(index: number): Promise<{ restored: number; deleted: number }> {
    if (index < 0 || index >= this.frames.length) throw new Error('no such checkpoint');
    let restored = 0, deleted = 0;
    for (let i = this.frames.length - 1; i >= index; i--) {
      for (const [path, prior] of this.frames[i].saved) {
        if (prior == null) { try { await this.fs.deleteFile(path); deleted++; } catch { /* already gone */ } }
        else { await this.fs.writeFile(path, prior); restored++; }
      }
    }
    this.frames.length = index; // drop the undone frames
    this.current = undefined;
    return { restored, deleted };
  }
}
